<template>
  <div
    class="rounded-3xl bg-semantic-primary-on-default"
    :data-testid="dataTestid"
  >
    <div class="rounded-t-3xl bg-semantic-surface-container-high p-4 md:px-6">
      <V2MoleculesTabs
        v-model="formModel.quoteType"
        :tabs="QUOTE_TYPE"
        size="base"
        variant="fill-width"
        class="!w-full max-w-none [&_li]:flex [&_li]:items-center [&_li]:justify-center [&_li]:px-5 [&_li>div]:justify-center [&_li>div]:!gap-1"
      >
      </V2MoleculesTabs>
    </div>

    <div class="py-6 px-4 md:px-6 md:pb-8">
      <form
        class="text-semantic-sizing-label-lg flex flex-col gap-6"
        @submit.prevent="handleSubmit"
      >
        <div class="flex flex-col gap-2">
          <div class="flex gap-1 items-center">
            <strong>Where are you going?</strong>

            <V2MoleculesPopover>
              <template #title>
                {{ TOOLTIPS.destination?.title }}
              </template>

              <template #description>
                {{ TOOLTIPS.destination?.description }}
              </template>
            </V2MoleculesPopover>
          </div>

          <V2AtomsDropdown
            v-model="formModel.destinations"
            :data-arr="DESTINATIONS_BY_GROUP"
            label=""
            placeholder="Type or choose your destination"
            data-input-display="text"
            data-list-display="text"
            :error-message="validation?.destinations?.errorMessage"
            :is-success="validation?.destinations?.isSuccess"
            multiple
            searchable
            @input="travelInsuranceStore.handleValidateForm(formModel, validation, 'destinations')"
          />
        </div>

        <div
          v-if="formModel.quoteType === 'single'"
          class="flex flex-col gap-2"
        >
          <div class="flex gap-1 items-center">
            <strong>When are you going?</strong>
            <V2MoleculesPopover>
              <template #title>
                {{ TOOLTIPS.daterange?.title }}
              </template>

              <template #description>
                {{ TOOLTIPS.daterange?.description }}
              </template>
            </V2MoleculesPopover>
          </div>

          <V2AtomsDaterangePicker
            v-model:start-date="formModel.leavingDate"
            v-model:end-date="formModel.returnDate"
            label=""
            placeholder="Select start and end dates of your trip"
            mobile-keyboard-type="numeric"
            data-testid="travel-widget-trip-dates"
            :disable-date="disablePreviousDate"
            :start-error-message="validation?.leavingDate?.errorMessage"
            :start-is-success="validation?.leavingDate?.isSuccess"
            :end-error-message="validation?.returnDate?.errorMessage"
            :end-is-success="validation?.returnDate?.isSuccess"
            @input="handleTripDatesInput"
          />

        </div>

        <div
          v-else
          class="flex gap-4"
        >
          <div class="flex flex-1 flex-col gap-2">
            <div class="flex gap-1 items-center">
              <strong>Commencement date</strong>

              <V2MoleculesPopover>
                <template #title>
                  {{ TOOLTIPS.commencementDate?.title }}
                </template>

                <template #description>
                  {{ TOOLTIPS.commencementDate?.description }}
                </template>
              </V2MoleculesPopover>
            </div>

            <V2AtomsDatepicker
              v-model="formModel.commencementDate"
              placeholder="DD/MM/YYYY"
              type="date"
              model-type="date"
              mobile-keyboard-type="numeric"
              :error-message="validation?.commencementDate?.errorMessage"
              :is-success="validation?.commencementDate?.isSuccess"
              :disable-date="disablePreviousDate"
              @input="travelInsuranceStore.handleValidateForm(formModel, validation, 'commencementDate')"
            />
          </div>

          <div class="flex flex-1 flex-col gap-2">
            <div class="flex gap-1 items-center">
              <strong>Max trip length (days)</strong>

              <V2MoleculesPopover>
                <template #title>
                  {{ TOOLTIPS.maxTripLength?.title }}
                </template>

                <template #description>
                  {{ TOOLTIPS.maxTripLength?.description }}
                </template>
              </V2MoleculesPopover>
            </div>

            <V2AtomsDropdown
              v-model="formModel.maxTripLength"
              placeholder="Select trip duration"
              label=""
              data-value="value"
              class="!min-h-12"
              :data-arr="MAX_TRIP_LENGTH"
              :error-message="validation?.maxTripLength?.errorMessage"
              :is-success="validation?.maxTripLength?.isSuccess"
              @input="travelInsuranceStore.handleValidateForm(formModel, validation, 'maxTripLength')"
            />
          </div>
        </div>

        <V2MoleculesTravelInsuranceTravellerAges
          ref="travellerAgesRef"
          v-model:adult-ages="formModel.adultAges"
          v-model:dependant-ages="formModel.dependantAges"
          data-testid="travel-widget-traveller-ages"
        />

        <div 
          class="flex flex-col gap-2"
        >
          <div class="flex gap-1 items-center">
            <strong>During your trip will you need cover for</strong>
          </div>

          <V2AtomsSelection
            v-model="formModel.extraCovers"
            :options="EXTRA_COVERS"
            type="checkbox"
            list-wrapper-class="!flex-row"
            inline
            ticker-position="middle"
            :error-message="validation?.extraCovers?.errorMessage"
            :is-success="validation?.extraCovers?.isSuccess"
            @update="travelInsuranceStore.handleValidateForm(formModel, validation, 'extraCovers')"
          />
        </div>

        <div class="email flex flex-col gap-2">
          <div class="flex gap-1 items-center">
            <p>
              <strong>Email</strong><template v-if="!isMandatoryEmail"> (optional)</template>
            </p>
          </div>

          <V2AtomsInput
            v-model="formModel.email"
            placeholder=""
            label=""
            data-testid="travel-widget-email"
            :error-message="validation?.email?.errorMessage"
            :is-success="validation?.email?.isSuccess"
            @input="travelInsuranceStore.handleValidateForm(formModel, validation, 'email', { emailRequired: isMandatoryEmail })"
          />
        </div>

        <p class="text-semantic-sizing-label-sm text-center text-semantic-surface-on-surface-variant">
          By clicking “Compare Now”, I acknowledge that I have read and agree to the <router-link to="/terms-of-use/" class="font-bold underline">Terms of Use</router-link>, the <router-link to="/privacy-policy/" class="font-bold underline">Privacy Policy</router-link>, and the <router-link to="/collection-notice/" class="font-bold underline">Collection Notice.</router-link>
        </p>

        <V2AtomsButton
          has-icon-right
          full
          type="submit"
          data-testid="travel-widget-submit"
        >
          Compare Now
        </V2AtomsButton>
      </form>
    </div>
  </div>
</template>

<script setup>
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js'

import _isEmpty from 'underscore/cjs/isEmpty.js'

import { storeToRefs } from 'pinia'

import {
  QUOTE_TYPE,
  TOOLTIPS
} from '~/constants/pages/travel-insurance'

import {
  DESTINATIONS_BY_GROUP
} from '~/constants/destinations.constants'

import {
  MAX_TRIP_LENGTH,
  EXTRA_COVERS,
  FORM_MODEL_OBJ
} from '~/constants/funnel/travel-insurance'

import {
  GENERATE_FORM_MODEL_TEST,
  handleScrollToError
} from '~/helpers/formfunnel.helpers'

import { useTravelMandatoryEmailExperiment } from '~/composables/travel/use-travel-mandatory-email-experiment'

dayjs.extend(customParseFormat)
dayjs.extend(isSameOrAfter)

defineOptions({
  name: 'OrganismsFormTravelInsuranceWidget'
})

defineProps({
  dataTestid: {
    type: String,
    default: ''
  }
})

const { $toast } = useNuxtApp()
const $router = useRouter()

const layoutStore = useLayoutsStore()
const userStore = useUserStore()
const travelInsuranceStore = useTravelInsuranceStore()
const {
  form,
  formUpdate,
  leadParams,
  leadUpdateParams,
  UUID
} = storeToRefs(travelInsuranceStore)

// CC-40297 — mandatory email A/B test; Control keeps email optional
const {
  isMandatoryEmail,
  resolve: resolveMandatoryEmail
} = useTravelMandatoryEmailExperiment()

const formModel = ref(Object.assign({}, FORM_MODEL_OBJ))
const travellerAgesRef = ref(null)
const validation = ref(GENERATE_FORM_MODEL_TEST({
  type: 'validation',
  form: Object.keys(FORM_MODEL_OBJ),
  keyTransform: 'default'
}))

watch(() => formModel.value.quoteType, async curr => {
  await nextTick()

  if (curr === 'single') {
    formModel.value.commencementDate = null
    formModel.value.maxTripLength = null
  } else {
    formModel.value.leavingDate = null
    formModel.value.returnDate = null
  }
})

onMounted(() => {
  // not awaited: the form stays usable, and submit enforces the arm on screen
  resolveMandatoryEmail()

  if (!_isEmpty(form.value)) {
    formModel.value = JSON.parse(JSON.stringify(form.value))
  }

  if (!formModel.value.email && userStore.isUserLoggedIn) {
    formModel.value.email = userStore.getUserProfile?.email || ''
  }
})

function handleTripDatesInput ({ field }) {
  if (field === 'startDate') {
    travelInsuranceStore.handleValidateForm(formModel.value, validation.value, 'leavingDate')

    if (formModel.value?.returnDate) {
      travelInsuranceStore.handleValidateForm(formModel.value, validation.value, 'returnDate')
    }

    return
  }

  travelInsuranceStore.handleValidateForm(formModel.value, validation.value, 'returnDate')
}

async function handleSubmit () {
  await travelInsuranceStore.handleValidateForm(formModel.value, validation.value, undefined, {
    emailRequired: isMandatoryEmail.value
  })

  const _VALIDATION = Object.assign({}, validation.value)

  // per-row traveller UI owns age validation; no .adult-ages element
  // exists for handleScrollToError's selector
  delete _VALIDATION?.adultAges
  delete _VALIDATION?.dependantAges

  if (formModel.value.quoteType === 'single') {
    delete _VALIDATION?.commencementDate
    delete _VALIDATION?.maxTripLength
  } else {
    delete _VALIDATION?.leavingDate
    delete _VALIDATION?.returnDate
  }

  const hasError = await handleScrollToError(_VALIDATION)
  const travellersValid = travellerAgesRef.value?.validate({ focus: !hasError }) ?? false

  if (hasError || !travellersValid) {
    return
  }
  
  layoutStore.setPageLoader(true)

  if (!UUID.value) {
    form.value = JSON.parse(JSON.stringify(formModel.value))

    await travelInsuranceStore.createLead(leadParams.value)
      .then(uuid => {
        if (uuid) {
          $router.push(`/travel-insurance/quote-results/?uuid=${uuid}`)
        }
        
        layoutStore.setPageLoader(false)
      })
      .catch(() => {
        $toast.error('An error occurred while creating your lead. Please try again.')
        layoutStore.setPageLoader(false)
      })

    return
  }

  formUpdate.value = JSON.parse(JSON.stringify(formModel.value))

  await travelInsuranceStore.updateLead({
    uuid: UUID.value,
    ...leadUpdateParams.value
  })
    .then(uuid => {
      // fall back to the stored UUID — updating an existing lead
      // shouldn't depend on the API echoing the uuid back
      const targetUuid = uuid || UUID.value

      if (targetUuid) {
        $router.push(`/travel-insurance/quote-results/?uuid=${targetUuid}`)
      }

      layoutStore.setPageLoader(false)
    })
    .catch(() => {
      $toast.error('An error occurred while updating lead. Please try again.')
      layoutStore.setPageLoader(false)
    })
}
</script>
